<script setup>
import Dialog from 'primevue/dialog';
import {computed, inject, reactive, ref, watch} from "vue";
import {useBreakpoint} from "@/composables/useBreakpoint";
import {Icon} from "@iconify/vue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {useConfigStore} from "@/stores/config.js";
import {useConfigData} from "@/composables/useConfigData.js";
import {showMessage} from "@/utils/showMessage.js";
import {keepNonEmptyValues} from "@/utils/helpers.js";
import {useSupplierStockStore} from "@/stores/supplierStock.js";

const emit = defineEmits(['refresh']);
const visible = inject('isShowAddMenuModal')

const supplierStockStore = useSupplierStockStore();
const configStore = useConfigStore()
const {isXL} = useBreakpoint();
const saving = ref(false)
const form = reactive({
  "game": "",
  "server": "",
})
const rules = computed(() => {
  return {
    game: {required},
    server: {required}
  };
});
const $vulidate = useVuelidate(rules, form);

/* ---------- Select options ---------- */
const {games} = useConfigData('Currency')

const selectedGame = computed(() => games.value.find(g => g.game === form.game) || null)

const servers = computed(() => selectedGame.value ? selectedGame.value.servers : [])
const addMenu = async () => {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  const payload = keepNonEmptyValues(form)
  if (!payload) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  try {
    saving.value = true
    const res = await supplierStockStore.action_addMenu(form);
    if (res.result === true) {
      showMessage('New Menu Set!', 'success')
      emit('refresh', true)
      visible.value = false;
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }

}

function resetForm() {
  form.game = "";
  form.server = "";
  $vulidate.value.$reset()
}

function onGameChange() {
  form.server = servers.value.length === 1 ? servers.value[0] : ''
}

watch(
    () => form.game,
    () => onGameChange()
)
</script>

<template>
  <Dialog v-model:visible="visible" @hide="resetForm"
          :class="['min-w-max min-h-max !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-screen max-w-screen-sm max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">Add Menu</div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button type="button" class="text-white-dark outline-none hover:text-red-500" @click="closeCallback">
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 h-full grid grid-cols-1 gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm">
      <!-- ================== Game ================== -->
      <div class="flex flex-col gap-1 text-xs 2xl:text-sm">
        <FloatLabel class="w-full custom-select"
                    :class="{ 'has-error': $vulidate.game.$error }"
                    variant="on">
          <Select v-model="form.game" inputId="game" :options="games" optionLabel="game"
                  option-value="game"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                  :class="[$vulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                  input-class="!text-xs 2xl:!text-sm"
                  filter autoFilterFocus/>
          <label for="game" class="!bg-slate-900">Game</label>
        </FloatLabel>
        <template v-if="$vulidate.game.$error">
          <p class="text-red-300 text-[.6rem] 2xl:text-xs"
             v-if="!$vulidate.game.required.$response">
            This field is required
          </p>
        </template>
      </div>

      <!-- ================== Server ================== -->
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
        <FloatLabel class="w-full custom-select"
                    :class="{ 'has-error': $vulidate.server.$error }"
                    variant="on">
          <Select v-model="form.server" inputId="server" :options="servers"
                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                  :class="[$vulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                  input-class="!text-xs 2xl:!text-sm"
                  :virtualScrollerOptions="{ itemSize: 38 }"
                  filter autoFilterFocus/>
          <label for="server" class="!bg-slate-900">Server</label>
        </FloatLabel>
        <template v-if="$vulidate.server.$error">
          <p class="text-red-300 mt-1 text-xs"
             v-if="!$vulidate.server.required.$response">
            This field is required
          </p>
        </template>
      </div>
    </div>

    <button @click="addMenu" :disabled="saving"
            class="btn btn-outline-primary flex flex-row gap-4 py-1 w-max self-end items-center">
      <svg v-if="saving" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
           class="lucide lucide-loader-icon lucide-loader animate-spin">
        <path d="M12 2v4"/>
        <path d="m16.2 7.8 2.9-2.9"/>
        <path d="M18 12h4"/>
        <path d="m16.2 16.2 2.9 2.9"/>
        <path d="M12 18v4"/>
        <path d="m4.9 19.1 2.9-2.9"/>
        <path d="M2 12h4"/>
        <path d="m4.9 4.9 2.9 2.9"/>
      </svg>
      Save
    </button>
  </Dialog>
</template>

<style>
@import "@/assets/primevue.css";
</style>
<style>
.p-dialog-header {
  @apply flex flex-row gap-10 !px-4 !py-0 border-b border-slate-700 h-full
}

.p-dialog-header-actions {
  @apply flex flex-row items-center gap-4 h-full
}
</style>
